import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { ApiService, ResourceName } from '../core/api.service';

type FieldType = 'text' | 'number' | 'date' | 'textarea' | 'select';
type OptionSource = 'empresas' | 'sectores' | 'activos' | 'vehiculos' | 'bpm-personal' | 'areas' | 'tipos-inspeccion' | 'tipos-vehiculo' | 'tipos-bpm-personal' | 'roles';

interface FieldConfig {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  source?: OptionSource;
  options?: { value: string | number; label: string }[];
}

interface ResourceConfig {
  idKeys: string[];
  fields?: FieldConfig[];
  pdfType?: string;
  buildPayload?: (form: Record<string, unknown>) => Record<string, unknown>;
}

const TEXT = 'text' as const;
const SELECT = 'select' as const;
const DATE = 'date' as const;

const DOCUMENT_TYPES = [
  { value: 'programas', label: 'Programa' },
  { value: 'procedimientos', label: 'Procedimiento' },
  { value: 'politicas', label: 'Politica' },
  { value: 'formatos', label: 'Formato' },
  { value: 'instructivos', label: 'Instructivo' },
  { value: 'otros', label: 'Otro' }
];

const COMPANY_FIELDS: FieldConfig[] = [
  { key: 'idSector', label: 'Sector', type: SELECT, source: 'sectores', required: true },
  { key: 'idEmpresa', label: 'Empresa', type: SELECT, source: 'empresas', required: true }
];

const CONFIG: Partial<Record<ResourceName, ResourceConfig>> = {
  inspecciones: {
    idKeys: ['idInspeccion'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idArea', label: 'Area', type: SELECT, source: 'areas', required: true },
      { key: 'idTipoInspeccion', label: 'Tipo de inspeccion', type: SELECT, source: 'tipos-inspeccion', required: true },
      { key: 'idPeriodicidad', label: 'Periodicidad', type: 'number', required: true },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' }
    ],
    buildPayload: (form) => ({ ...form, detalles: [] })
  },
  mantenimientos: {
    idKeys: ['idMantenimiento', 'idPreventivo'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idActivo', label: 'Activo', type: SELECT, source: 'activos', required: true },
      {
        key: 'tipoMant',
        label: 'Tipo mantenimiento',
        type: SELECT,
        required: true,
        options: [
          { value: 'Correctivo', label: 'Correctivo' },
          { value: 'Preventivo', label: 'Preventivo' }
        ]
      },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' },
      { key: 'detalleDescripcion', label: 'Detalle', type: 'textarea', required: true },
      { key: 'detalleComentario', label: 'Comentario del detalle', type: 'textarea' }
    ],
    buildPayload: (form) => ({
      idActivo: form['idActivo'],
      idEmpresa: form['idEmpresa'],
      tipoMant: form['tipoMant'],
      observaciones: form['observaciones'],
      detalles: [
        {
          descripcion: form['detalleDescripcion'],
          comentario: form['detalleComentario'],
          orden: 1
        }
      ]
    })
  },
  fallas: {
    idKeys: ['idReporte'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idActivo', label: 'Activo', type: SELECT, source: 'activos', required: true },
      {
        key: 'tipoFalla',
        label: 'Tipo de falla',
        type: SELECT,
        required: true,
        options: [
          { value: 'Mecanica', label: 'Mecanica' },
          { value: 'Electrica', label: 'Electrica' },
          { value: 'Operativa', label: 'Operativa' },
          { value: 'Otra', label: 'Otra' }
        ]
      },
      {
        key: 'prioridad',
        label: 'Prioridad',
        type: SELECT,
        required: true,
        options: [
          { value: 'Baja', label: 'Baja' },
          { value: 'Media', label: 'Media' },
          { value: 'Alta', label: 'Alta' },
          { value: 'Critica', label: 'Critica' }
        ]
      },
      {
        key: 'estado',
        label: 'Estado',
        type: SELECT,
        options: [
          { value: 'Abierto', label: 'Abierto' },
          { value: 'En proceso', label: 'En proceso' },
          { value: 'Resuelto', label: 'Resuelto' }
        ]
      },
      { key: 'tecnicoAsignado', label: 'Tecnico asignado', type: TEXT },
      { key: 'descripcion', label: 'Descripcion', type: 'textarea', required: true },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' },
      { key: 'accionTomada', label: 'Accion tomada', type: 'textarea' }
    ],
    buildPayload: (form) => ({
        idActivo: form['idActivo'],
        idEmpresa: form['idEmpresa'],
        tipoFalla: form['tipoFalla'],
        prioridad: form['prioridad'],
        estado: form['estado'],
        accionTomada: form['accionTomada'],
        tecnicoAsignado: form['tecnicoAsignado'],
        descripcion: form['descripcion'],
        observaciones: form['observaciones']
      })
  },
  activos: {
    idKeys: ['idActivo'],
    fields: [
      ...COMPANY_FIELDS,
      {
        key: 'tipoActivo',
        label: 'Tipo activo',
        type: SELECT,
        required: true,
        options: [
          { value: 'Maquinaria', label: 'Maquinaria' },
          { value: 'Infraestructura', label: 'Infraestructura' },
          { value: 'Elemento', label: 'Elemento' }
        ]
      },
      { key: 'referencia', label: 'Referencia', type: TEXT, required: true },
      { key: 'nombreEquipo', label: 'Nombre del equipo', type: TEXT, required: true },
      { key: 'marca', label: 'Marca', type: TEXT },
      { key: 'modelo', label: 'Modelo', type: TEXT },
      { key: 'numeroSerie', label: 'Numero de serie', type: TEXT },
      { key: 'ubicacion', label: 'Ubicacion', type: TEXT },
      { key: 'area', label: 'Area', type: TEXT },
      { key: 'fechaAdquisicion', label: 'Fecha adquisicion', type: 'date' },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' }
    ]
  },
  pqrs: {
    idKeys: ['idPqrs'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'nombre', label: 'Nombre', type: TEXT, required: true },
      { key: 'cedula', label: 'Cedula', type: TEXT, required: true },
      { key: 'correo', label: 'Correo', type: TEXT, required: true },
      { key: 'celular', label: 'Celular', type: TEXT, required: true },
      {
        key: 'medioRespuesta',
        label: 'Medio respuesta',
        type: SELECT,
        options: [
          { value: 'Correo', label: 'Correo' },
          { value: 'Telefono', label: 'Telefono' },
          { value: 'Fisico', label: 'Fisico' }
        ]
      },
      { key: 'observacion', label: 'Observacion', type: 'textarea', required: true },
      { key: 'accionInmediata', label: 'Accion inmediata', type: 'textarea' },
      { key: 'lugarReporte', label: 'Lugar reporte', type: TEXT },
      { key: 'loteAfectado', label: 'Lote afectado', type: TEXT },
      { key: 'cantidadAfectada', label: 'Cantidad afectada', type: 'number' }
    ]
  },
  vehiculos: {
    idKeys: ['idVehiculo'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idTipoVehiculo', label: 'Tipo vehiculo', type: SELECT, source: 'tipos-vehiculo', required: true },
      { key: 'placa', label: 'Placa', type: TEXT, required: true },
      { key: 'marca', label: 'Marca', type: TEXT },
      { key: 'modelo', label: 'Modelo', type: TEXT },
      { key: 'linea', label: 'Linea', type: TEXT },
      { key: 'color', label: 'Color', type: TEXT },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' }
    ]
  },
  checklists: {
    idKeys: ['idChecklist'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idVehiculo', label: 'Vehiculo', type: SELECT, source: 'vehiculos', required: true }
    ],
    buildPayload: (form) => ({ idEmpresa: form['idEmpresa'], idVehiculo: form['idVehiculo'], detalles: [] })
  },
  'bpm-personal': {
    idKeys: ['idPersonaBpm'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idTipoBpmPersonal', label: 'Tipo BPM', type: SELECT, source: 'tipos-bpm-personal', required: true },
      { key: 'identificacion', label: 'Identificacion', type: TEXT, required: true },
      { key: 'nombreCompleto', label: 'Nombre completo', type: TEXT, required: true },
      { key: 'cargo', label: 'Cargo', type: TEXT },
      { key: 'area', label: 'Area', type: TEXT },
      {
        key: 'periodicidad',
        label: 'Periodicidad',
        type: SELECT,
        options: [
          { value: 'Diaria', label: 'Diaria' },
          { value: 'Semanal', label: 'Semanal' },
          { value: 'Mensual', label: 'Mensual' }
        ]
      },
      { key: 'diasIntervalo', label: 'Dias intervalo', type: 'number' }
    ]
  },
  'chequeos-bpm-personal': {
    idKeys: ['idChequeoBpmPersonal'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idPersonaBpm', label: 'Persona', type: SELECT, source: 'bpm-personal', required: true }
    ],
    buildPayload: (form) => ({ idEmpresa: form['idEmpresa'], idPersonaBpm: form['idPersonaBpm'], detalles: [] })
  },
  'tipos-bpm-personal': {
    idKeys: ['idTipoBpmPersonal'],
    fields: [
      { key: 'nombre', label: 'Nombre del tipo BPM', type: TEXT, required: true },
      { key: 'descripcion', label: 'Descripcion', type: 'textarea' }
    ],
    buildPayload: (form) => ({
      nombre: form['nombre'],
      descripcion: form['descripcion'],
      items: []
    })
  },
  'control-proceso': {
    idKeys: ['idControlProceso'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'nombreArea', label: 'Area', type: TEXT, required: true },
      { key: 'nombreControl', label: 'Control', type: TEXT, required: true },
      { key: 'parametroNombre', label: 'Parametro inicial', type: TEXT, required: true },
      {
        key: 'parametroTipoDato',
        label: 'Tipo de dato',
        type: SELECT,
        options: [
          { value: 'Texto', label: 'Texto' },
          { value: 'Numero', label: 'Numero' },
          { value: 'Fecha', label: 'Fecha' },
          { value: 'SiNo', label: 'Si/No' }
        ]
      }
    ],
    buildPayload: (form) => ({
      idEmpresa: form['idEmpresa'],
      nombreArea: form['nombreArea'],
      nombreControl: form['nombreControl'],
      parametros: [
        {
          nombre: form['parametroNombre'],
          tipoDato: form['parametroTipoDato'] || 'Texto',
          orden: 1
        }
      ]
    })
  },
  programas: {
    idKeys: ['idProgramas'],
    pdfType: 'programas'
  },
  empresas: {
    idKeys: ['idEmpresa'],
    fields: [
      { key: 'idSector', label: 'Sector', type: SELECT, source: 'sectores', required: true },
      { key: 'nombre', label: 'Empresa', type: TEXT, required: true },
      { key: 'nit', label: 'NIT', type: TEXT, required: true },
      { key: 'fechaInicio', label: 'Fecha inicio', type: DATE, required: true },
      { key: 'fechaFin', label: 'Fecha fin', type: DATE, required: true },
      {
        key: 'vigente',
        label: 'Estado',
        type: SELECT,
        options: [
          { value: 1, label: 'Activo' },
          { value: 0, label: 'Inactivo' }
        ]
      }
    ],
    buildPayload: (form) => ({
      nombre: form['nombre'],
      nit: form['nit'],
      fechaInicio: form['fechaInicio'],
      fechaFin: form['fechaFin'],
      vigente: form['vigente'] ?? 1,
      estadoVigencia: form['vigente'] ?? 1,
      idSector: form['idSector']
    })
  },
  sectores: {
    idKeys: ['idSector'],
    fields: [
      { key: 'nombre', label: 'Sector', type: TEXT, required: true },
      { key: 'descripcion', label: 'Descripcion', type: TEXT }
    ],
    buildPayload: (form) => ({
      nombre: form['nombre'],
      descripcion: form['descripcion']
    })
  },
  usuarios: {
    idKeys: ['idUsuario'],
    fields: [
      ...COMPANY_FIELDS,
      { key: 'idRol', label: 'Rol', type: SELECT, source: 'roles', required: true },
      { key: 'nombres', label: 'Nombres', type: TEXT, required: true },
      { key: 'apellidos', label: 'Apellidos', type: TEXT, required: true },
      { key: 'cedula', label: 'Cedula', type: TEXT, required: true },
      { key: 'celular', label: 'Celular', type: TEXT },
      { key: 'correo', label: 'Correo', type: TEXT },
      { key: 'contrasena', label: 'Contrasena', type: TEXT },
      {
        key: 'vigente',
        label: 'Estado',
        type: SELECT,
        options: [
          { value: 1, label: 'Activo' },
          { value: 0, label: 'Inactivo' }
        ]
      }
    ],
    buildPayload: (form) => ({
      idUsuario: form['idUsuario'] ?? 0,
      nombres: form['nombres'],
      apellidos: form['apellidos'],
      cedula: form['cedula'],
      celular: form['celular'],
      correo: form['correo'],
      contrasena: form['contrasena'],
      vigente: form['vigente'] ?? 1,
      idSector: form['idSector'],
      idEmpresa: form['idEmpresa'],
      idRol: form['idRol']
    })
  }
};

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="page">
      <div class="title-row">
        <div>
          <p>Modulo</p>
          <h1>{{ title() }}</h1>
        </div>
        <div class="actions">
          @if (canOpenForm()) {
            <button class="btn secondary" (click)="openCreate()">Nuevo</button>
          }
          <button class="btn" (click)="load()">Actualizar</button>
        </div>
      </div>

      @if (resource === 'programas' && canManagePrograms()) {
        <form class="upload-panel" (ngSubmit)="uploadProgram()">
          <label class="field">
            <span>Tipo PDF</span>
            <select name="programDocType" [(ngModel)]="programDocType" (ngModelChange)="onProgramTypeChange()" required>
              @for (type of documentTypes; track type.value) {
                <option [ngValue]="type.value">{{ type.label }}</option>
              }
            </select>
          </label>
          <label class="field">
            <span>Sector</span>
            <select name="programSector" [(ngModel)]="programSectorId" (ngModelChange)="onProgramSectorChange()" required>
              <option [ngValue]="0">Selecciona sector</option>
              @for (sector of sectores(); track idValue(sector, 'idSector')) {
                <option [ngValue]="numberValue(sector, 'idSector')">{{ textValue(sector, ['nombre', 'Nombre']) }}</option>
              }
            </select>
          </label>
          <label class="field">
            <span>Empresa</span>
            <select name="programEmpresa" [(ngModel)]="programEmpresaId" required>
              <option [ngValue]="0">Selecciona empresa</option>
              @for (empresa of empresasSector(); track idValue(empresa, 'idEmpresa')) {
                <option [ngValue]="numberValue(empresa, 'idEmpresa')">{{ textValue(empresa, ['nombre', 'Nombre', 'nombreEmpresa']) }}</option>
              }
            </select>
          </label>
          <label class="field">
            <span>Nombre</span>
            <input name="programName" [(ngModel)]="programName" required>
          </label>
          @if (programDocType !== 'programas') {
            <label class="field">
              <span>Programa relacionado</span>
              <select name="relatedProgram" [(ngModel)]="programRelatedId" required>
                <option [ngValue]="0">Selecciona programa</option>
                @for (programa of programOptions(); track idValue(programa, 'idProgramas')) {
                  <option [ngValue]="numberValue(programa, 'idProgramas')">{{ textValue(programa, ['nombre', 'Nombre']) }}</option>
                }
              </select>
            </label>
          }
          <label class="field">
            <span>PDF</span>
            <input name="programFile" type="file" accept="application/pdf" (change)="onProgramFile($event)" required>
          </label>
          <button class="btn" [disabled]="uploading()">{{ uploading() ? 'Subiendo...' : 'Subir PDF' }}</button>
        </form>
      }

      @if (resource === 'programas') {
        <div class="tabs">
          @for (type of documentTypes; track type.value) {
            <button class="tab" [class.active]="programListType === type.value" (click)="setProgramListType(type.value)">
              {{ type.label }}
            </button>
          }
        </div>
      }

      @if (resource === 'vehiculos' || resource === 'checklists' || resource === 'bpm-personal' || resource === 'chequeos-bpm-personal') {
        <form class="filter-panel" (ngSubmit)="load()">
          @if (auth.user()?.idRol === 1) {
            <label class="field">
              <span>Sector</span>
              <select name="vehicleFilterSector" [(ngModel)]="vehicleFilterSectorId" (ngModelChange)="onVehicleSectorChange()">
                <option [ngValue]="0">Todos</option>
                @for (sector of sectores(); track idValue(sector, 'idSector')) {
                  <option [ngValue]="numberValue(sector, 'idSector')">{{ textValue(sector, ['nombre', 'Nombre']) }}</option>
                }
              </select>
            </label>
            <label class="field">
              <span>Empresa</span>
              <select name="vehicleFilterEmpresa" [(ngModel)]="vehicleFilterEmpresaId" (ngModelChange)="onVehicleEmpresaChange()">
                <option [ngValue]="0">Empresa activa</option>
                @for (empresa of empresasSector(); track idValue(empresa, 'idEmpresa')) {
                  <option [ngValue]="numberValue(empresa, 'idEmpresa')">{{ textValue(empresa, ['nombre', 'Nombre', 'nombreEmpresa']) }}</option>
                }
              </select>
            </label>
          }
          @if (resource === 'checklists' || resource === 'chequeos-bpm-personal') {
            <label class="field">
              <span>{{ resource === 'checklists' ? 'Vehiculo' : 'Persona' }}</span>
              <select name="historyVehicle" [(ngModel)]="vehicleFilterVehiculoId">
                <option [ngValue]="0">Todos</option>
                @if (resource === 'checklists') {
                  @for (vehiculo of vehiculos(); track idValue(vehiculo, 'idVehiculo')) {
                    <option [ngValue]="numberValue(vehiculo, 'idVehiculo')">{{ textValue(vehiculo, ['placa', 'Placa']) }}</option>
                  }
                } @else {
                  @for (persona of bpmPersonal(); track idValue(persona, 'idPersonaBpm')) {
                    <option [ngValue]="numberValue(persona, 'idPersonaBpm')">{{ textValue(persona, ['nombreCompleto', 'NombreCompleto', 'identificacion']) }}</option>
                  }
                }
              </select>
            </label>
          }
          <button class="btn">Filtrar</button>
        </form>
      }

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }

      @if (message()) {
        <p class="message">{{ message() }}</p>
      }

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              @for (column of columns(); track column) {
                <th>{{ label(column) }}</th>
              }
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row) {
              <tr>
                @for (column of columns(); track column) {
                  <td>{{ value(row, column) }}</td>
                }
                <td class="row-actions">
                  @if (pdfUrl(row)) {
                    <button class="mini" (click)="openPdf(row)">Ver PDF</button>
                  }
                  @if (imageUrl(row)) {
                    <button class="mini" (click)="openImage(row)">Ver foto</button>
                  }
                  @if (resource === 'vehiculos') {
                    <button class="mini" (click)="startChecklist(row)">Realizar checklist</button>
                    <button class="mini" (click)="showVehicleHistory(row)">Historial</button>
                  }
                  @if (resource === 'bpm-personal') {
                    <button class="mini" (click)="startBpmChecklist(row)">Realizar chequeo</button>
                    <button class="mini" (click)="showBpmHistory(row)">Historial</button>
                  }
                  @if (resource === 'checklists') {
                    <button class="mini" (click)="showChecklistDetails(row)">Detalles</button>
                  }
                  @if (resource === 'chequeos-bpm-personal') {
                    <button class="mini" (click)="showBpmDetails(row)">Detalles</button>
                  }
                  @if (canModifyCurrentResource() && idOf(row); as id) {
                    @if (resource !== 'programas' && canOpenForm()) {
                      <button class="mini" (click)="openEdit(row)">Editar</button>
                    }
                    @if (resource === 'programas' && programListType === 'programas') {
                      <button class="mini" (click)="toggleProgram(row, id)">{{ row['vigente'] === false ? 'Activar' : 'Desactivar' }}</button>
                    }
                    <button class="mini danger" (click)="remove(row, id)">Eliminar</button>
                  }
                </td>
              </tr>
            } @empty {
              <tr>
                <td [attr.colspan]="columns().length + 1">Sin registros para mostrar.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (editing()) {
        <div class="modal-backdrop">
          <form class="modal" (ngSubmit)="saveForm()">
            <div>
              <p>{{ editingId() ? 'Editar registro' : 'Nuevo registro' }}</p>
              <h2>{{ title() }}</h2>
            </div>

            <div class="form-grid">
              @for (field of fields(); track field.key) {
                <label class="field" [class.wide]="field.type === 'textarea'">
                  <span>{{ field.label }}</span>
                  @if (field.type === 'select') {
                    <select
                      [name]="field.key"
                      [(ngModel)]="form[field.key]"
                      (ngModelChange)="onFieldChange(field.key)"
                      [required]="field.required">
                      <option [ngValue]="emptySelectValue(field)">Selecciona {{ field.label.toLowerCase() }}</option>
                      @for (option of optionsFor(field); track option.value) {
                        <option [ngValue]="option.value">{{ option.label }}</option>
                      }
                    </select>
                  } @else if (field.type === 'textarea') {
                    <textarea [name]="field.key" [(ngModel)]="form[field.key]" rows="4" [required]="field.required"></textarea>
                  } @else {
                    <input [name]="field.key" [type]="field.type" [(ngModel)]="form[field.key]" [required]="field.required">
                  }
                </label>
              }
            </div>

            <div class="modal-actions">
              <button type="button" class="btn ghost" (click)="closeEditor()">Cancelar</button>
              <button class="btn">Guardar</button>
            </div>
          </form>
        </div>
      }

      @if (viewerUrl()) {
        <div class="modal-backdrop">
          <section class="viewer-modal">
            <div class="viewer-head">
              <h2>{{ viewerTitle() }}</h2>
              <button class="mini danger" (click)="closeViewer()">Cerrar</button>
            </div>
            @if (viewerKind() === 'pdf') {
              <iframe [src]="safeViewerUrl()" sandbox="allow-same-origin allow-scripts"></iframe>
            } @else {
              <img [src]="viewerUrl()" [alt]="viewerTitle()">
            }
          </section>
        </div>
      }

      @if (checklistModal()) {
        <div class="modal-backdrop">
          <form class="modal checklist-modal" (ngSubmit)="selectedBpmPerson() ? saveBpmChecklist() : saveChecklist()">
            <div class="viewer-head">
              <div>
                <p>{{ selectedBpmPerson() ? 'Chequeo BPM Personal' : 'Checklist vehiculo' }}</p>
                <h2>{{ selectedBpmPerson() ? textValue(selectedBpmPerson() || {}, ['nombreCompleto', 'NombreCompleto', 'identificacion']) : textValue(selectedVehicle() || {}, ['placa', 'Placa']) }}</h2>
              </div>
              <button type="button" class="mini danger" (click)="closeChecklistModal()">Cerrar</button>
            </div>
            <div class="form-grid">
              <label class="field">
                <span>{{ selectedBpmPerson() ? 'Responsable' : 'Conductor' }}</span>
                <input name="checklistConductor" [(ngModel)]="checklistConductor">
              </label>
              <label class="field">
                <span>Observaciones generales</span>
                <input name="checklistObservaciones" [(ngModel)]="checklistObservaciones">
              </label>
            </div>
            <div class="checklist-items">
              @for (item of checklistItems(); track numberValue(item, 'idItem')) {
                <section class="check-item">
                  <div>
                    <strong>{{ textValue(item, ['nombre', 'Nombre', 'nombreItem', 'NombreItem']) }}</strong>
                    <span>{{ textValue(item, ['descripcion', 'Descripcion']) }}</span>
                  </div>
                  <select [name]="'cumple' + numberValue(item, 'idItem')" [(ngModel)]="checklistAnswers[numberValue(item, 'idItem')].cumple">
                    <option [ngValue]="1">Si</option>
                    <option [ngValue]="0">No</option>
                    <option [ngValue]="-1">N/A</option>
                  </select>
                  <input [name]="'obs' + numberValue(item, 'idItem')" placeholder="Observacion" [(ngModel)]="checklistAnswers[numberValue(item, 'idItem')].observacion">
                  <input [name]="'foto' + numberValue(item, 'idItem')" type="file" accept="image/*" (change)="onChecklistPhoto($event, numberValue(item, 'idItem'))">
                </section>
              }
            </div>
            <div class="modal-actions">
              <button type="button" class="btn ghost" (click)="closeChecklistModal()">Cancelar</button>
              <button class="btn">{{ selectedBpmPerson() ? 'Guardar chequeo' : 'Guardar checklist' }}</button>
            </div>
          </form>
        </div>
      }

      @if (historyModal()) {
        <div class="modal-backdrop">
          <section class="modal">
            <div class="viewer-head">
              <div>
                <p>Historial</p>
                <h2>{{ historyTitle() }}</h2>
              </div>
              <button class="mini danger" (click)="closeHistoryModal()">Cerrar</button>
            </div>
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>{{ historyIsBpm() ? 'Identificacion' : 'Placa' }}</th>
                    <th>{{ historyIsBpm() ? 'Persona' : 'Conductor' }}</th>
                    <th>Avance</th>
                    <th>Observaciones</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  @for (record of checklistHistory(); track idValue(record, historyIsBpm() ? 'idChequeoBpmPersonal' : 'idChecklist')) {
                    <tr>
                      <td>{{ value(record, 'fechaRevision') }}</td>
                      <td>{{ historyIsBpm() ? value(record, 'identificacion') : value(record, 'placa') }}</td>
                      <td>{{ historyIsBpm() ? value(record, 'nombreCompleto') : value(record, 'conductor') }}</td>
                      <td>{{ value(record, 'porcentajeAvance') }}%</td>
                      <td>{{ value(record, 'observaciones') }}</td>
                      <td><button class="mini" (click)="historyIsBpm() ? showBpmDetails(record) : showChecklistDetails(record)">Detalles</button></td>
                    </tr>
                  } @empty {
                    <tr><td colspan="6">Sin historial para mostrar.</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </section>
        </div>
      }

      @if (detailsModal()) {
        <div class="modal-backdrop">
          <section class="modal">
            <div class="viewer-head">
              <div>
                <p>Detalle checklist</p>
                <h2>{{ detailsTitle() }}</h2>
              </div>
              <button class="mini danger" (click)="closeDetailsModal()">Cerrar</button>
            </div>
            <div class="checklist-items">
              @for (detail of checklistDetails(); track idValue(detail, 'idDetalle')) {
                <section class="check-item">
                  <div>
                    <strong>{{ textValue(detail, ['nombreItem', 'NombreItem']) }}</strong>
                    <span>{{ value(detail, 'observacion') }}</span>
                  </div>
                  <span>{{ cumpleLabel(detail) }}</span>
                  @if (imageUrl(detail)) {
                    <button class="mini" (click)="openImage(detail)">Ver foto</button>
                  }
                </section>
              } @empty {
                <p>Sin detalles para mostrar.</p>
              }
            </div>
          </section>
        </div>
      }
    </section>
  `,
  styles: [`
    .page {
      display: grid;
      gap: 18px;
    }

    .title-row,
    .actions,
    .row-actions,
    .modal-actions {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    .title-row {
      justify-content: space-between;
    }

    p,
    h1,
    h2 {
      margin: 0;
    }

    .title-row p,
    .modal p {
      color: var(--muted);
    }

    .secondary {
      background: #12815d;
    }

    .ghost {
      background: #eef2f7;
      color: var(--ink);
    }

    .upload-panel,
    .filter-panel {
      display: grid;
      grid-template-columns: repeat(5, minmax(150px, 1fr)) auto;
      gap: 12px;
      align-items: end;
      padding: 14px;
      border: 1px solid var(--line);
      border-radius: 8px;
      background: white;
    }

    .filter-panel {
      grid-template-columns: repeat(3, minmax(180px, 260px)) auto;
      justify-content: start;
    }

    .error {
      color: var(--bad);
      font-weight: 700;
    }

    .tabs {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .tab {
      min-height: 34px;
      padding: 8px 12px;
      border: 1px solid var(--line);
      border-radius: 6px;
      background: white;
      color: var(--ink);
      font-weight: 700;
    }

    .tab.active {
      background: #172033;
      color: white;
      border-color: #172033;
    }

    .message {
      color: var(--ok);
      font-weight: 700;
    }

    .row-actions {
      min-width: 220px;
    }

    .mini {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 30px;
      padding: 6px 9px;
      border-radius: 6px;
      background: #edf4ff;
      color: #174fc0;
      font-size: 12px;
      font-weight: 700;
      text-decoration: none;
      border: 0;
    }

    .mini.danger {
      background: #fff0ee;
      color: var(--bad);
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      display: grid;
      place-items: center;
      padding: 20px;
      background: rgba(15, 23, 42, .42);
      z-index: 5;
    }

    .modal {
      display: grid;
      gap: 16px;
      width: min(920px, 100%);
      max-height: calc(100vh - 40px);
      overflow: auto;
      padding: 20px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 24px 60px rgba(15, 23, 42, .22);
    }

    .viewer-modal {
      display: grid;
      grid-template-rows: auto minmax(0, 1fr);
      gap: 12px;
      width: min(1120px, 100%);
      height: min(760px, calc(100vh - 40px));
      padding: 16px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 24px 60px rgba(15, 23, 42, .22);
    }

    .viewer-head {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
    }

    iframe,
    .viewer-modal img {
      width: 100%;
      height: 100%;
      border: 1px solid var(--line);
      border-radius: 6px;
      background: #f8fafc;
    }

    .viewer-modal img {
      object-fit: contain;
      padding: 10px;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
    }

    .field.wide {
      grid-column: 1 / -1;
    }

    textarea {
      width: 100%;
      resize: vertical;
      border: 1px solid var(--line);
      border-radius: 6px;
      padding: 12px;
      font: inherit;
    }

    .modal-actions {
      justify-content: flex-end;
    }

    .checklist-modal {
      width: min(1180px, 100%);
    }

    .checklist-items {
      display: grid;
      gap: 10px;
    }

    .check-item {
      display: grid;
      grid-template-columns: minmax(220px, 1fr) 110px minmax(180px, 1fr) minmax(160px, auto);
      gap: 10px;
      align-items: center;
      padding: 12px;
      border: 1px solid var(--line);
      border-radius: 8px;
      background: #f8fafc;
    }

    .check-item div {
      display: grid;
      gap: 3px;
    }

    .check-item span {
      color: var(--muted);
      font-size: 12px;
    }

    @media (max-width: 980px) {
      .upload-panel,
      .filter-panel,
      .check-item,
      .form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class ListPageComponent implements OnInit {
  readonly rows = signal<Record<string, unknown>[]>([]);
  readonly sectores = signal<Record<string, unknown>[]>([]);
  readonly empresasSector = signal<Record<string, unknown>[]>([]);
  readonly activos = signal<Record<string, unknown>[]>([]);
  readonly vehiculos = signal<Record<string, unknown>[]>([]);
  readonly bpmPersonal = signal<Record<string, unknown>[]>([]);
  readonly areas = signal<Record<string, unknown>[]>([]);
  readonly tiposInspeccion = signal<Record<string, unknown>[]>([]);
  readonly tiposVehiculo = signal<Record<string, unknown>[]>([]);
  readonly tiposBpmPersonal = signal<Record<string, unknown>[]>([]);
  readonly roles = signal<Record<string, unknown>[]>([]);
  readonly programOptions = signal<Record<string, unknown>[]>([]);
  readonly error = signal('');
  readonly message = signal('');
  readonly title = signal('');
  readonly editing = signal(false);
  readonly editingId = signal<string | number | null>(null);
  readonly uploading = signal(false);
  readonly viewerUrl = signal('');
  readonly viewerTitle = signal('');
  readonly viewerKind = signal<'pdf' | 'image'>('pdf');
  readonly checklistModal = signal(false);
  readonly historyModal = signal(false);
  readonly detailsModal = signal(false);
  readonly selectedVehicle = signal<Record<string, unknown> | null>(null);
  readonly selectedBpmPerson = signal<Record<string, unknown> | null>(null);
  readonly checklistItems = signal<Record<string, unknown>[]>([]);
  readonly checklistHistory = signal<Record<string, unknown>[]>([]);
  readonly checklistDetails = signal<Record<string, unknown>[]>([]);
  readonly historyTitle = signal('');
  readonly detailsTitle = signal('');
  readonly documentTypes = DOCUMENT_TYPES;
  resource: ResourceName = 'inspecciones';
  form: Record<string, unknown> = {};
  vehicleFilterSectorId = 0;
  vehicleFilterEmpresaId = 0;
  vehicleFilterVehiculoId = 0;
  checklistConductor = '';
  checklistObservaciones = '';
  checklistAnswers: Record<number, { cumple: number; observacion: string; fotoBase64?: string; fotoExtension?: string }> = {};
  programName = '';
  programSectorId = 0;
  programEmpresaId = 0;
  programRelatedId = 0;
  programDocType = 'programas';
  programListType = 'programas';
  private programFile: File | null = null;

  readonly columns = computed(() => {
    const first = this.rows()[0];
    if (!first) {
      return ['estado'];
    }

    return Object.keys(first)
      .filter((key) => !Array.isArray(first[key]) && typeof first[key] !== 'object')
      .filter((key) => !['url', 'pdfUrl', 'fotoUrl', 'FotoUrl', 'fotoActualFalla', 'FotoActualFalla', 'evidenciaUrl', 'fichaTecnicaUrl'].includes(key))
      .filter((key) => !this.hideIdColumn(key, first))
      .slice(0, 9);
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: ApiService,
    readonly auth: AuthService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.resource = data['resource'] as ResourceName;
      this.title.set(data['title'] as string);
      this.programListType = 'programas';
      this.programDocType = 'programas';
      this.load();
      this.loadBaseOptions();
    });
  }

  load(): void {
    this.error.set('');
    this.message.set('');
    if (this.resource === 'programas' && this.programListType !== 'programas') {
      this.api.listDocuments(this.programListType).subscribe({
        next: (rows) => this.rows.set(rows as Record<string, unknown>[]),
        error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudieron cargar los documentos.')
      });
      return;
    }

    const params = this.listParams();
    this.api.list(this.resource, params).subscribe({
      next: (rows) => this.rows.set(rows as Record<string, unknown>[]),
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudieron cargar los registros.')
    });
  }

  loadBaseOptions(): void {
    this.api.list('sectores').subscribe({
      next: (rows) => this.sectores.set(rows as Record<string, unknown>[]),
      error: () => this.sectores.set([])
    });

    if (this.auth.user()?.idRol === 2) {
      const idEmpresa = this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0;
      const nombre = this.auth.user()?.nombreEmpresaActiva ?? this.auth.user()?.nombreEmpresa ?? 'Mi empresa';
      this.empresasSector.set([{ idEmpresa, nombre }]);
    }

    if (this.resource === 'programas') {
      this.loadProgramOptions();
    }

    if (this.resource === 'usuarios') {
      this.loadRoles();
    }

    if (this.resource === 'vehiculos' || this.resource === 'checklists' || this.resource === 'bpm-personal' || this.resource === 'chequeos-bpm-personal') {
      this.loadVehicleFilterOptions();
    }
  }

  canCreate(): boolean {
    return this.canModifyCurrentResource() && (!!CONFIG[this.resource]?.fields || this.resource === 'programas');
  }

  private listParams(): Record<string, number> | undefined {
    if (this.resource !== 'vehiculos' && this.resource !== 'checklists' && this.resource !== 'bpm-personal' && this.resource !== 'chequeos-bpm-personal') {
      return undefined;
    }

    const params: Record<string, number> = {};
    const idEmpresa = this.auth.user()?.idRol === 2
      ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
      : this.vehicleFilterEmpresaId;

    if (idEmpresa) {
      params['idEmpresa'] = idEmpresa;
    }

    if (this.resource === 'checklists' && this.vehicleFilterVehiculoId) {
      params['idVehiculo'] = this.vehicleFilterVehiculoId;
    }

    if (this.resource === 'chequeos-bpm-personal' && this.vehicleFilterVehiculoId) {
      params['idPersonaBpm'] = this.vehicleFilterVehiculoId;
    }

    return params;
  }

  loadVehicleFilterOptions(): void {
    const idEmpresa = this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0;
    if (this.auth.user()?.idRol === 2) {
      this.vehicleFilterEmpresaId = idEmpresa;
      this.api.list('vehiculos', { idEmpresa }).subscribe({
        next: (rows) => this.vehiculos.set(rows as Record<string, unknown>[]),
        error: () => this.vehiculos.set([])
      });
      this.api.list('bpm-personal', { idEmpresa }).subscribe({
        next: (rows) => this.bpmPersonal.set(rows as Record<string, unknown>[]),
        error: () => this.bpmPersonal.set([])
      });
      return;
    }

    this.vehicleFilterEmpresaId = this.vehicleFilterEmpresaId || idEmpresa;
    if (this.vehicleFilterEmpresaId) {
      this.api.list('vehiculos', { idEmpresa: this.vehicleFilterEmpresaId }).subscribe({
        next: (rows) => this.vehiculos.set(rows as Record<string, unknown>[]),
        error: () => this.vehiculos.set([])
      });
      this.api.list('bpm-personal', { idEmpresa: this.vehicleFilterEmpresaId }).subscribe({
        next: (rows) => this.bpmPersonal.set(rows as Record<string, unknown>[]),
        error: () => this.bpmPersonal.set([])
      });
    }
  }

  onVehicleSectorChange(): void {
    this.vehicleFilterEmpresaId = 0;
    this.vehicleFilterVehiculoId = 0;
    this.vehiculos.set([]);
    this.bpmPersonal.set([]);
    if (!this.vehicleFilterSectorId) {
      return;
    }

    this.api.empresasPorSector(this.vehicleFilterSectorId).subscribe({
      next: (rows) => this.empresasSector.set(rows as Record<string, unknown>[]),
      error: () => this.empresasSector.set([])
    });
  }

  onVehicleEmpresaChange(): void {
    this.vehicleFilterVehiculoId = 0;
    if (!this.vehicleFilterEmpresaId) {
      this.vehiculos.set([]);
      this.bpmPersonal.set([]);
      return;
    }

    this.api.list('vehiculos', { idEmpresa: this.vehicleFilterEmpresaId }).subscribe({
      next: (rows) => this.vehiculos.set(rows as Record<string, unknown>[]),
      error: () => this.vehiculos.set([])
    });
    this.api.list('bpm-personal', { idEmpresa: this.vehicleFilterEmpresaId }).subscribe({
      next: (rows) => this.bpmPersonal.set(rows as Record<string, unknown>[]),
      error: () => this.bpmPersonal.set([])
    });
  }

  canOpenForm(): boolean {
    return !!CONFIG[this.resource]?.fields && this.canModifyCurrentResource();
  }

  canEdit(): boolean {
    return this.auth.user()?.idRol === 1 || this.auth.user()?.idRol === 2;
  }

  canManagePrograms(): boolean {
    return this.auth.user()?.idRol === 1;
  }

  canModifyCurrentResource(): boolean {
    if (this.resource === 'empresas' || this.resource === 'sectores') {
      return this.auth.user()?.idRol === 1;
    }

    return this.resource === 'programas' ? this.canManagePrograms() : this.canEdit();
  }

  fields(): FieldConfig[] {
    return CONFIG[this.resource]?.fields ?? [];
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form = {};

    for (const field of this.fields()) {
      if (field.key === 'idEmpresa') {
        this.form[field.key] = this.auth.user()?.idRol === 2
          ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
          : 0;
      } else if (field.key === 'idSector') {
        this.form[field.key] = this.auth.user()?.idRol === 2 ? this.auth.user()?.idSector ?? 0 : 0;
      } else if (field.type === 'number') {
        this.form[field.key] = 0;
      } else if (field.options?.length) {
        this.form[field.key] = field.options[0].value;
      } else {
        this.form[field.key] = '';
      }
    }

    this.loadDependentOptions();
    this.editing.set(true);
  }

  openEdit(row: Record<string, unknown>): void {
    this.editingId.set(this.idOf(row));
    this.form = {};

    for (const field of this.fields()) {
      this.form[field.key] = this.initialValue(row, field);
    }

    if (!this.form['idSector']) {
      this.form['idSector'] = this.findSectorFromRow(row);
    }

    this.loadDependentOptions();
    this.editing.set(true);
  }

  closeEditor(): void {
    this.editing.set(false);
    this.editingId.set(null);
    this.form = {};
  }

  saveForm(): void {
    const missing = this.fields().find((field) => field.required && !this.form[field.key]);
    if (missing) {
      this.error.set(`Completa el campo ${missing.label}.`);
      return;
    }

    const builder = CONFIG[this.resource]?.buildPayload;
    const payload = builder ? builder(this.form) : { ...this.form };
    if (this.resource !== 'usuarios' && this.resource !== 'empresas') {
      delete payload['idSector'];
    }

    const id = this.editingId();
    const request = id
      ? this.api.update(this.resource, id, payload)
      : this.api.create(this.resource, payload);

    request.subscribe({
      next: () => {
        this.message.set('Registro guardado correctamente.');
        this.closeEditor();
        this.load();
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo guardar el registro.')
    });
  }

  remove(row: Record<string, unknown>, id: string | number): void {
    const name = row['nombre'] || row['Nombre'] || row['nombreEquipo'] || row['nombreCompleto'] || row['placa'] || id;
    if (!confirm(`Eliminar ${name}?`)) {
      return;
    }

    const request = this.resource === 'programas'
      ? this.programListType === 'programas'
        ? this.api.deleteProgram(id)
        : this.api.deleteDocument(this.programListType, id)
      : this.api.delete(this.resource, id);

    request.subscribe({
      next: () => {
        this.message.set('Registro eliminado correctamente.');
        this.load();
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo eliminar el registro.')
    });
  }

  toggleProgram(row: Record<string, unknown>, id: string | number): void {
    const vigente = row['vigente'] === false;
    this.api.setProgramVigente(id, vigente).subscribe({
      next: () => {
        this.message.set(vigente ? 'Programa activado.' : 'Programa desactivado.');
        this.load();
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo actualizar el programa.')
    });
  }

  startChecklist(vehicle: Record<string, unknown>): void {
    this.error.set('');
    this.selectedVehicle.set(vehicle);
    this.checklistConductor = '';
    this.checklistObservaciones = '';
    this.checklistAnswers = {};
    const idTipo = this.numberValue(vehicle, 'idTipoVehiculo');
    if (!idTipo) {
      this.error.set('El vehiculo no tiene tipo asociado.');
      return;
    }

    this.api.tipoVehiculo(idTipo).subscribe({
      next: (tipo) => {
        const items = (tipo['items'] ?? tipo['Items'] ?? []) as Record<string, unknown>[];
        this.checklistItems.set(items);
        for (const item of items) {
          this.checklistAnswers[this.numberValue(item, 'idItem')] = { cumple: 1, observacion: '' };
        }
        this.checklistModal.set(true);
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo cargar la checklist del tipo de vehiculo.')
    });
  }

  saveChecklist(): void {
    const vehicle = this.selectedVehicle();
    if (!vehicle) {
      return;
    }

    const detalles = this.checklistItems().map((item) => {
      const idItem = this.numberValue(item, 'idItem');
      const answer = this.checklistAnswers[idItem] ?? { cumple: -1, observacion: '' };
      return {
        idItem,
        cumple: answer.cumple,
        observacion: answer.observacion,
        fotoBase64: answer.fotoBase64,
        fotoExtension: answer.fotoExtension
      };
    });

    const idEmpresa = this.auth.user()?.idRol === 2
      ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
      : this.numberValue(vehicle, 'idEmpresa') || this.vehicleFilterEmpresaId;

    const payload = {
      idEmpresa,
      idVehiculo: this.numberValue(vehicle, 'idVehiculo'),
      idTipoVehiculo: this.numberValue(vehicle, 'idTipoVehiculo'),
      placa: this.textValue(vehicle, ['placa', 'Placa']),
      conductor: this.checklistConductor,
      observaciones: this.checklistObservaciones,
      detalles
    };

    this.api.create('checklists', payload).subscribe({
      next: () => {
        this.message.set('Checklist registrado correctamente.');
        this.closeChecklistModal();
        this.load();
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo guardar la checklist.')
    });
  }

  onChecklistPhoto(event: Event, idItem: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result ?? '');
      const base64 = value.includes(',') ? value.split(',').pop() ?? '' : value;
      const ext = file.name.split('.').pop() || 'jpg';
      this.checklistAnswers[idItem] = {
        ...(this.checklistAnswers[idItem] ?? { cumple: 1, observacion: '' }),
        fotoBase64: base64,
        fotoExtension: ext
      };
    };
    reader.readAsDataURL(file);
  }

  showVehicleHistory(vehicle: Record<string, unknown>): void {
    const idVehiculo = this.numberValue(vehicle, 'idVehiculo');
    const idEmpresa = this.auth.user()?.idRol === 2
      ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
      : this.numberValue(vehicle, 'idEmpresa') || this.vehicleFilterEmpresaId;

    this.api.list('checklists', { idEmpresa, idVehiculo }).subscribe({
      next: (rows) => {
        this.checklistHistory.set(rows as Record<string, unknown>[]);
        this.historyTitle.set(this.textValue(vehicle, ['placa', 'Placa']));
        this.historyModal.set(true);
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo cargar el historial.')
    });
  }

  showChecklistDetails(row: Record<string, unknown>): void {
    const idChecklist = this.numberValue(row, 'idChecklist');
    const idEmpresa = this.auth.user()?.idRol === 2
      ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
      : this.numberValue(row, 'idEmpresa') || this.vehicleFilterEmpresaId;

    this.api.checklistDetalles(idChecklist, idEmpresa).subscribe({
      next: (rows) => {
        this.checklistDetails.set(rows as Record<string, unknown>[]);
        this.detailsTitle.set(this.textValue(row, ['placa', 'Placa']));
        this.detailsModal.set(true);
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudieron cargar los detalles.')
    });
  }

  startBpmChecklist(person: Record<string, unknown>): void {
    this.error.set('');
    this.selectedBpmPerson.set(person);
    this.selectedVehicle.set(null);
    this.checklistConductor = '';
    this.checklistObservaciones = '';
    this.checklistAnswers = {};
    const idTipo = this.numberValue(person, 'idTipoBpmPersonal');
    if (!idTipo) {
      this.error.set('La persona no tiene tipo BPM asociado.');
      return;
    }

    this.api.tipoBpmPersonal(idTipo).subscribe({
      next: (tipo) => {
        const items = (tipo['items'] ?? tipo['Items'] ?? []) as Record<string, unknown>[];
        this.checklistItems.set(items);
        for (const item of items) {
          this.checklistAnswers[this.numberValue(item, 'idItem')] = { cumple: 1, observacion: '' };
        }
        this.checklistModal.set(true);
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo cargar el chequeo BPM.')
    });
  }

  saveBpmChecklist(): void {
    const person = this.selectedBpmPerson();
    if (!person) {
      return;
    }

    const detalles = this.checklistItems().map((item) => {
      const idItem = this.numberValue(item, 'idItem');
      const answer = this.checklistAnswers[idItem] ?? { cumple: -1, observacion: '' };
      return {
        idItem,
        cumple: answer.cumple,
        observacion: answer.observacion,
        fotoBase64: answer.fotoBase64,
        fotoExtension: answer.fotoExtension
      };
    });

    const idEmpresa = this.auth.user()?.idRol === 2
      ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
      : this.numberValue(person, 'idEmpresa') || this.vehicleFilterEmpresaId;

    const payload = {
      idEmpresa,
      idPersonaBpm: this.numberValue(person, 'idPersonaBpm'),
      idTipoBpmPersonal: this.numberValue(person, 'idTipoBpmPersonal'),
      identificacion: this.textValue(person, ['identificacion', 'Identificacion']),
      nombreCompleto: this.textValue(person, ['nombreCompleto', 'NombreCompleto']),
      cargo: this.textValue(person, ['cargo', 'Cargo']),
      area: this.textValue(person, ['area', 'Area']),
      observaciones: this.checklistObservaciones || this.checklistConductor,
      detalles
    };

    this.api.create('chequeos-bpm-personal', payload).subscribe({
      next: () => {
        this.message.set('Chequeo BPM registrado correctamente.');
        this.closeChecklistModal();
        this.load();
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo guardar el chequeo BPM.')
    });
  }

  showBpmHistory(person: Record<string, unknown>): void {
    const idPersonaBpm = this.numberValue(person, 'idPersonaBpm');
    const idEmpresa = this.auth.user()?.idRol === 2
      ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
      : this.numberValue(person, 'idEmpresa') || this.vehicleFilterEmpresaId;

    this.api.list('chequeos-bpm-personal', { idEmpresa, idPersonaBpm }).subscribe({
      next: (rows) => {
        this.checklistHistory.set(rows as Record<string, unknown>[]);
        this.historyTitle.set(this.textValue(person, ['nombreCompleto', 'NombreCompleto', 'identificacion']));
        this.historyModal.set(true);
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo cargar el historial BPM.')
    });
  }

  showBpmDetails(row: Record<string, unknown>): void {
    const idChequeo = this.numberValue(row, 'idChequeoBpmPersonal');
    const idEmpresa = this.auth.user()?.idRol === 2
      ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
      : this.numberValue(row, 'idEmpresa') || this.vehicleFilterEmpresaId;

    this.api.chequeoBpmPersonalDetalles(idChequeo, idEmpresa).subscribe({
      next: (rows) => {
        this.checklistDetails.set(rows as Record<string, unknown>[]);
        this.detailsTitle.set(this.textValue(row, ['nombreCompleto', 'NombreCompleto', 'identificacion']));
        this.detailsModal.set(true);
      },
      error: (err) => this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudieron cargar los detalles BPM.')
    });
  }

  closeChecklistModal(): void {
    this.checklistModal.set(false);
    this.selectedVehicle.set(null);
    this.selectedBpmPerson.set(null);
    this.checklistItems.set([]);
    this.checklistAnswers = {};
  }

  historyIsBpm(): boolean {
    return this.resource === 'bpm-personal' || this.resource === 'chequeos-bpm-personal' || !!this.selectedBpmPerson();
  }

  closeHistoryModal(): void {
    this.historyModal.set(false);
    this.checklistHistory.set([]);
  }

  closeDetailsModal(): void {
    this.detailsModal.set(false);
    this.checklistDetails.set([]);
  }

  onFieldChange(key: string): void {
    if (key === 'idSector') {
      this.form['idEmpresa'] = this.auth.user()?.idRol === 2
        ? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0
        : 0;
      this.empresasSector.set([]);
      this.activos.set([]);
      this.vehiculos.set([]);
      this.areas.set([]);
      this.tiposInspeccion.set([]);
      this.tiposVehiculo.set([]);
      this.tiposBpmPersonal.set([]);
      this.bpmPersonal.set([]);
      this.loadEmpresasPorSector(Number(this.form['idSector'] ?? 0));
    }

    if (key === 'idEmpresa') {
      this.loadDependentOptions();
    }
  }

  loadDependentOptions(): void {
    const idSector = Number(this.form['idSector'] ?? 0);
    const idEmpresa = Number(this.form['idEmpresa'] ?? this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0);

    if (idSector) {
      this.loadEmpresasPorSector(idSector);
    }

    if (idEmpresa) {
      this.api.list('activos', { idEmpresa }).subscribe({
        next: (rows) => this.activos.set(rows as Record<string, unknown>[]),
        error: () => this.activos.set([])
      });
      this.api.list('vehiculos', { idEmpresa }).subscribe({
        next: (rows) => this.vehiculos.set(rows as Record<string, unknown>[]),
        error: () => this.vehiculos.set([])
      });
      this.api.list('bpm-personal', { idEmpresa }).subscribe({
        next: (rows) => this.bpmPersonal.set(rows as Record<string, unknown>[]),
        error: () => this.bpmPersonal.set([])
      });
      this.api.list('areas', { idEmpresa }).subscribe({
        next: (rows) => this.areas.set(rows as Record<string, unknown>[]),
        error: () => this.areas.set([])
      });
      this.api.list('tipos-inspeccion', { idEmpresa }).subscribe({
        next: (rows) => this.tiposInspeccion.set(rows as Record<string, unknown>[]),
        error: () => this.tiposInspeccion.set([])
      });
      this.api.list('tipos-vehiculo', { idEmpresa }).subscribe({
        next: (rows) => this.tiposVehiculo.set(rows as Record<string, unknown>[]),
        error: () => this.tiposVehiculo.set([])
      });
      this.api.list('tipos-bpm-personal', { idEmpresa }).subscribe({
        next: (rows) => this.tiposBpmPersonal.set(rows as Record<string, unknown>[]),
        error: () => this.tiposBpmPersonal.set([])
      });
    }
  }

  loadEmpresasPorSector(idSector: number): void {
    if (this.auth.user()?.idRol === 2) {
      const idEmpresa = this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0;
      const nombre = this.auth.user()?.nombreEmpresaActiva ?? this.auth.user()?.nombreEmpresa ?? 'Mi empresa';
      this.empresasSector.set(idEmpresa ? [{ idEmpresa, nombre }] : []);
      return;
    }

    if (!idSector) {
      this.empresasSector.set([]);
      return;
    }

    this.api.empresasPorSector(idSector).subscribe({
      next: (rows) => this.empresasSector.set(rows as Record<string, unknown>[]),
      error: () => this.empresasSector.set([])
    });
  }

  onProgramSectorChange(): void {
    this.programEmpresaId = 0;
    if (this.auth.user()?.idRol === 2) {
      const idEmpresa = this.auth.user()?.idEmpresaActiva ?? this.auth.user()?.idEmpresa ?? 0;
      const nombre = this.auth.user()?.nombreEmpresaActiva ?? this.auth.user()?.nombreEmpresa ?? 'Mi empresa';
      this.empresasSector.set([{ idEmpresa, nombre }]);
      this.programEmpresaId = idEmpresa;
      return;
    }

    this.loadEmpresasPorSector(this.programSectorId);
  }

  onProgramTypeChange(): void {
    if (this.programDocType === 'programas') {
      this.programRelatedId = 0;
    }
  }

  setProgramListType(type: string): void {
    this.programListType = type;
    this.load();
  }

  loadProgramOptions(): void {
    this.api.list('programas').subscribe({
      next: (rows) => this.programOptions.set(rows as Record<string, unknown>[]),
      error: () => this.programOptions.set([])
    });
  }

  loadRoles(): void {
    this.api.list('roles').subscribe({
      next: (rows) => this.roles.set((rows as Record<string, unknown>[])
        .filter((row) => this.auth.user()?.idRol === 1 || this.numberValue(row, 'idRol') !== 1)),
      error: () => this.roles.set(this.auth.user()?.idRol === 1
        ? [{ idRol: 1, nombre: 'Superusuario' }, { idRol: 2, nombre: 'Administrador' }, { idRol: 3, nombre: 'Usuario' }]
        : [{ idRol: 2, nombre: 'Administrador' }, { idRol: 3, nombre: 'Usuario' }])
    });
  }

  onProgramFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.programFile = input.files?.[0] ?? null;
  }

  uploadProgram(): void {
    if (!this.programSectorId || !this.programEmpresaId || !this.programName.trim() || !this.programFile) {
      this.error.set('Completa sector, empresa, nombre y archivo PDF.');
      return;
    }

    if (this.programDocType !== 'programas' && !this.programRelatedId) {
      this.error.set('Selecciona el programa relacionado.');
      return;
    }

    this.uploading.set(true);
    this.error.set('');
    this.api.uploadProgram({
      tipo: this.programDocType,
      nombre: this.programName.trim(),
      idSector: this.programSectorId,
      idEmpresa: this.programEmpresaId,
      idProgramas: this.programRelatedId,
      archivo: this.programFile
    }).subscribe({
      next: () => {
        this.message.set('PDF subido correctamente.');
        this.programName = '';
        this.programSectorId = 0;
        this.programEmpresaId = 0;
        this.programRelatedId = 0;
        this.programFile = null;
        this.uploading.set(false);
        this.programListType = this.programDocType;
        this.loadProgramOptions();
        this.load();
      },
      error: (err) => {
        this.error.set(err?.error?.mensaje ?? err.message ?? 'No se pudo subir el PDF.');
        this.uploading.set(false);
      }
    });
  }

  optionsFor(field: FieldConfig): { value: string | number; label: string }[] {
    if (field.options) {
      return field.options;
    }

    const source = field.source;
    if (source === 'sectores') {
      return this.sectores().map((row) => ({ value: this.numberValue(row, 'idSector'), label: this.textValue(row, ['nombre', 'Nombre']) }));
    }
    if (source === 'empresas') {
      return this.empresasSector().map((row) => ({ value: this.numberValue(row, 'idEmpresa'), label: this.textValue(row, ['nombre', 'Nombre', 'nombreEmpresa', 'NombreEmpresa']) }));
    }
    if (source === 'activos') {
      return this.activos().map((row) => ({ value: this.numberValue(row, 'idActivo'), label: this.textValue(row, ['nombreEquipo', 'NombreEquipo', 'referencia']) }));
    }
    if (source === 'vehiculos') {
      return this.vehiculos().map((row) => ({ value: this.numberValue(row, 'idVehiculo'), label: this.textValue(row, ['placa', 'Placa', 'nombreTipo', 'NombreTipo']) }));
    }
    if (source === 'bpm-personal') {
      return this.bpmPersonal().map((row) => ({ value: this.numberValue(row, 'idPersonaBpm'), label: this.textValue(row, ['nombreCompleto', 'NombreCompleto', 'identificacion']) }));
    }
    if (source === 'areas') {
      return this.areas().map((row) => ({ value: this.numberValue(row, 'idArea'), label: this.textValue(row, ['nombre', 'Nombre', 'nombreArea']) }));
    }
    if (source === 'tipos-inspeccion') {
      return this.tiposInspeccion().map((row) => ({ value: this.numberValue(row, 'idTipoInspeccion'), label: this.textValue(row, ['nombre', 'Nombre', 'nombreTipo']) }));
    }
    if (source === 'tipos-vehiculo') {
      return this.tiposVehiculo().map((row) => ({ value: this.numberValue(row, 'idTipoVehiculo'), label: this.textValue(row, ['nombre', 'Nombre', 'nombreTipo']) }));
    }
    if (source === 'tipos-bpm-personal') {
      return this.tiposBpmPersonal().map((row) => ({ value: this.numberValue(row, 'idTipoBpmPersonal'), label: this.textValue(row, ['nombre', 'Nombre', 'nombreTipo']) }));
    }
    if (source === 'roles') {
      return this.roles().map((row) => ({ value: this.numberValue(row, 'idRol'), label: this.textValue(row, ['nombre', 'Nombre', 'descripcion', 'Descripcion', 'nombreRol']) }));
    }

    return [];
  }

  emptySelectValue(field: FieldConfig): string | number {
    return field.options?.length ? '' : 0;
  }

  idOf(row: Record<string, unknown>): string | number | null {
    if (this.resource === 'programas' && this.programListType !== 'programas') {
      const id = row['id'] ?? row['Id'];
      if (typeof id === 'number' || typeof id === 'string') {
        return id;
      }
    }

    for (const key of CONFIG[this.resource]?.idKeys ?? []) {
      const value = row[key];
      if (typeof value === 'number' || typeof value === 'string') {
        return value;
      }
    }

    return null;
  }

  pdfUrl(row: Record<string, unknown>): string {
    const config = CONFIG[this.resource];
    const raw = (row['url'] || row['evidenciaUrl'] || row['fichaTecnicaUrl'] || row['pdfUrl']) as string | undefined;
    if (!raw) {
      return '';
    }

    if (this.resource === 'programas' && this.programListType !== 'programas') {
      return this.api.pdfUrl(this.programListType, raw);
    }

    if (config?.pdfType) {
      return this.api.pdfUrl(config.pdfType, raw);
    }

    if (this.resource === 'activos' && row['fichaTecnicaUrl']) {
      return this.api.pdfUrl('fichas', raw);
    }

    if (this.resource === 'mantenimientos' && this.isPdfEvidence(row, raw)) {
      return this.api.pdfUrl('mantenimientos', raw);
    }

    if (this.resource === 'pqrs') {
      const id = this.idOf(row);
      return id ? `/webapi/pdf/pqrs/${encodeURIComponent(String(id))}` : '';
    }

    return this.looksLikePdf(raw) ? this.api.pdfUrl('documentos', raw) : '';
  }

  imageUrl(row: Record<string, unknown>): string {
    const raw = (row['fotoUrl'] || row['FotoUrl'] || row['fotoActualFalla'] || row['FotoActualFalla']) as string | undefined;
    return raw ? this.api.fotoUrl(raw) : '';
  }

  openPdf(row: Record<string, unknown>): void {
    const url = this.pdfUrl(row);
    if (!url) {
      return;
    }

    this.viewerKind.set('pdf');
    this.viewerTitle.set(this.textValue(row, ['nombre', 'Nombre', 'nombreEquipo', 'placa', 'estado']) || 'PDF');
    this.viewerUrl.set(`${url}#toolbar=0&navpanes=0&scrollbar=1`);
  }

  openImage(row: Record<string, unknown>): void {
    const url = this.imageUrl(row);
    if (!url) {
      return;
    }

    this.viewerKind.set('image');
    this.viewerTitle.set(this.textValue(row, ['nombre', 'Nombre', 'nombreEquipo', 'placa', 'estado']) || 'Foto');
    this.viewerUrl.set(url);
  }

  closeViewer(): void {
    this.viewerUrl.set('');
  }

  safeViewerUrl(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.viewerUrl());
  }

  numberValue(row: Record<string, unknown>, key: string): number {
    return Number(row[key] ?? row[this.upperFirst(key)] ?? 0);
  }

  idValue(row: Record<string, unknown>, key: string): string | number {
    return this.numberValue(row, key);
  }

  textValue(row: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = row[key];
      if (value !== null && value !== undefined && value !== '') {
        return String(value);
      }
    }

    return 'Sin nombre';
  }

  value(row: Record<string, unknown>, column: string): string {
    const value = row[column];
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'boolean') {
      return value ? 'Si' : 'No';
    }

    return String(value);
  }

  cumpleLabel(row: Record<string, unknown>): string {
    const value = row['cumple'] ?? row['Cumple'];
    if (value === true) {
      return 'Si';
    }
    if (value === false) {
      return 'No';
    }
    return 'N/A';
  }

  label(value: string): string {
    return value
      .replace(/^id/i, 'Id ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase());
  }

  private initialValue(row: Record<string, unknown>, field: FieldConfig): unknown {
    if (field.key === 'detalleDescripcion') {
      const detail = (row['detalles'] as Record<string, unknown>[] | undefined)?.[0];
      return detail?.['descripcion'] ?? '';
    }
    if (field.key === 'detalleComentario') {
      const detail = (row['detalles'] as Record<string, unknown>[] | undefined)?.[0];
      return detail?.['comentario'] ?? '';
    }
    if (field.key === 'parametroNombre') {
      const param = (row['parametros'] as Record<string, unknown>[] | undefined)?.[0];
      return param?.['nombre'] ?? '';
    }
    if (field.key === 'parametroTipoDato') {
      const param = (row['parametros'] as Record<string, unknown>[] | undefined)?.[0];
      return param?.['tipoDato'] ?? 'Texto';
    }

    const value = row[field.key] ?? row[this.upperFirst(field.key)] ?? row[field.key.toUpperCase()];
    if (field.type === 'date' && typeof value === 'string') {
      return value.slice(0, 10);
    }

    return value ?? this.emptySelectValue(field);
  }

  private findSectorFromRow(row: Record<string, unknown>): number {
    return Number(row['idSector'] ?? row['IdSector'] ?? 0);
  }

  private hideIdColumn(key: string, row: Record<string, unknown>): boolean {
    const readable: Record<string, string[]> = {
      idEmpresa: ['nombreEmpresa', 'NombreEmpresa'],
      idSector: ['nombreSector', 'NombreSector'],
      idActivo: ['nombreActivo', 'NombreActivo'],
      idArea: ['nombreArea', 'NombreArea'],
      idTipoInspeccion: ['nombreTipo', 'NombreTipo']
    };

    return !!readable[key]?.some((name) => row[name] !== undefined);
  }

  private upperFirst(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private isPdfEvidence(row: Record<string, unknown>, raw: string): boolean {
    const tipo = String(row['tipoEvidencia'] ?? row['TipoEvidencia'] ?? '').toLowerCase();
    return tipo === 'pdf' || this.looksLikePdf(raw);
  }

  private looksLikePdf(value: string): boolean {
    return /\.pdf($|\?)/i.test(value);
  }
}
